/**
 * Chatzustand: Verlauf und Live-Nachrichten des gerade geöffneten Kanals.
 *
 * Nur ein Kanal ist je Zeitpunkt aktiv – wie beim Sim-Socket wechselt ein
 * neuer Kanal die Verbindung, statt eine zweite zu öffnen. `channel === 'lobby'`
 * ist der globale Chat, alles andere eine Partie-ID.
 */
import { create } from 'zustand';
import { api, type ChatMessageEntry } from '../lib/api';
import { connectChat, disconnectChat, sendChatMessage } from '../lib/chatSocket';

interface ChatState {
  channel: string | null;
  messages: ChatMessageEntry[];
  loading: boolean;
  error: string | null;
  open: (channel: string) => Promise<void>;
  close: () => void;
  send: (body: string) => void;
}

export const useChat = create<ChatState>((set, get) => ({
  channel: null,
  messages: [],
  loading: false,
  error: null,

  async open(channel) {
    set({ channel, messages: [], loading: true, error: null });
    connectChat(channel, (message) => {
      // Zwischenzeitlich gewechselt? Dann gehört die Nachricht nicht mehr hierher.
      if (get().channel !== channel) return;
      set((state) => ({ messages: [...state.messages, message] }));
    });
    try {
      const history = channel === 'lobby' ? await api.lobbyChat() : await api.gameChat(channel);
      if (get().channel !== channel) return;
      set({ messages: history, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  close() {
    disconnectChat();
    set({ channel: null, messages: [], loading: false, error: null });
  },

  send(body) {
    const channel = get().channel;
    const text = body.trim();
    if (!channel || !text) return;
    sendChatMessage(channel, text, (ack) => {
      if (!ack.ok) set({ error: ack.message ?? 'Nachricht konnte nicht gesendet werden.' });
    });
  },
}));
